"use client";

import { c as _c } from "react/compiler-runtime";
import type { CartOutputDto, CustomerAddressInputDto, ShippingInfoInputDto } from "@sticker-giant/sg-frontend-common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CustomerFacingErrorMessages } from "@/constants/CustomerFacingErrorMessages";
import { SavedInfoSource } from "@/constants/SavedInfoSource";
import { trackAddressEdited } from "@/lib/analytics/segment";
import { CART_ACTIVE_FILTER } from "@/queries/useCart";
import { useGlobalStore } from "@/stores/useGlobalStore";
import { useServicesStore } from "@/stores/useServicesStore";
export const updateCustomerAddressMutationKey = ["updateCustomerAddress"] as const;
interface UpdateCustomerAddressInput {
  shippingInfo: ShippingInfoInputDto;
  customerAddressInput: CustomerAddressInputDto;
}
interface UpdateCustomerAddressResult {
  cart: CartOutputDto;
  accountSaveSucceeded: boolean;
}
export function useUpdateCustomerAddressMutation() {
  const $ = _c(9);
  const cartClient = useServicesStore(_temp);
  const accountClient = useServicesStore(_temp2);
  const cartId = useGlobalStore(_temp3);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== accountClient || $[1] !== cartClient || $[2] !== cartId) {
    t0 = async t1 => {
      const {
        shippingInfo,
        customerAddressInput
      } = t1;
      if (!cartClient) {
        throw new Error("Cart client is not available.");
      }
      if (!accountClient) {
        throw new Error("Account client is not available.");
      }
      if (!cartId) {
        throw new Error("Cart id is not available.");
      }
      const accountPromise = accountClient.upsertCustomerAddress(customerAddressInput);
      const cartPromise = cartClient.updateShippingInfo(cartId, shippingInfo, CustomerFacingErrorMessages.unableToUpdateShippingAddressToCart);
      const [accountResult, cartResult] = await Promise.allSettled([accountPromise, cartPromise]);
      if (cartResult.status === "rejected") {
        throw cartResult.reason;
      }
      return {
        cart: cartResult.value,
        accountSaveSucceeded: accountResult.status === "fulfilled"
      };
    };
    $[0] = accountClient;
    $[1] = cartClient;
    $[2] = cartId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== queryClient) {
    t1 = (t2, t3) => {
      const {
        cart,
        accountSaveSucceeded
      } = t2;
      const {
        customerAddressInput: customerAddressInput_0
      } = t3;
      if (accountSaveSucceeded) {
        trackAddressEdited(SavedInfoSource.CHECKOUT);
      }
      queryClient.setQueriesData(CART_ACTIVE_FILTER, cart);
      queryClient.invalidateQueries({
        queryKey: ["getCustomerAddresses", customerAddressInput_0.customerId]
      });
    };
    $[4] = queryClient;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t0 || $[7] !== t1) {
    t2 = {
      mutationKey: updateCustomerAddressMutationKey,
      mutationFn: t0,
      onSuccess: t1
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return useMutation(t2);
}
function _temp3(state_1) {
  return state_1.cartId;
}
function _temp2(state_0) {
  return state_0.accountClientFetch;
}
function _temp(state) {
  return state.cartClient;
}